import type { StyleSystemProps } from '@withjoy/joykit';
import { Box, styled } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import type { CurationListProps } from './CurationList';

export const ITEM_ASPECT_RATIO = '32/27';
export const ITEM_SPACING = 24;

export const StyledContainer = styled(Box)``;
export const StyledPhoto = styled(Box)``;

export const ItemButton = styled(Box)<{ $isActive: boolean; $variant: CurationListProps['variant'] }>`
  cursor: ${({ $isActive = false }) => ($isActive ? 'unset' : 'pointer')};

  ${StyledPhoto}::after {
    border-color: ${({ $isActive = false, $variant, ...props }) => {
      const activeColor = $variant !== 'overview' ? 'white' : props.theme.colors.mono5;
      return $isActive ? activeColor : 'transparent';
    }};
  }
  @media (hover: hover) {
    ${StyledPhoto}::after {
      transition: ${animationTransition('border')};
    }
    &:hover {
      ${StyledPhoto}::after {
        border-color: ${({ $variant, ...props }) => ($variant !== 'overview' ? 'white' : props.theme.colors.mono3)};
      }
    }
    &:active {
      ${StyledPhoto}::after {
        border-color: ${({ $variant, ...props }) => ($variant !== 'overview' ? 'white' : props.theme.colors.mono5)};
      }
    }
  }
`;

const containerStyles: StyleSystemProps = {
  position: 'relative',
  zIndex: 1,
};

const itemButtonStyles: StyleSystemProps = {
  width: '100%',
  height: '100%',
  cursor: 'pointer',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
};

const curationsTitleStyles = (variant: CurationListProps['variant']): StyleSystemProps => ({
  color: variant === 'overview' ? 'mono14' : 'primary1',
  cursor: 'pointer',
  typographyVariant: {
    _: 'hed2',
    xs: 'hed3',
    sm2: 'hed4',
    xl: 'hed5',
  },
});

const curationsCtaStyles = (variant: CurationListProps['variant']): StyleSystemProps => ({
  typographyVariant: variant === 'overview' ? 'button2' : 'body2',
  fontWeight: 'semibold',
  color: variant === 'overview' ? 'mono14' : 'primary1',
  textDecoration: 'underline',
  cursor: 'pointer',
});

const photoStyles: StyleSystemProps = {
  width: '100%',
  paddingTop: '56.25%',
  flexGrow: 1,

  position: 'relative',
  borderRadius: 3,

  // ::before pseudo el will render the image
  _before: {
    content: '" "',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: 3,
  },

  // ::after pseudo element will render the border
  _after: {
    content: '" "',
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    borderWidth: '2px',
    borderStyle: 'solid',
    borderRadius: 'inherit',
  },
};

const itemTitleStyles = (variant: CurationListProps['variant']): StyleSystemProps => ({
  color: variant === 'overview' ? 'mono14' : 'primary1',
  marginTop: 3,
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '100%',
});

const itemDescriptionStyles = (variant: CurationListProps['variant']): StyleSystemProps => ({
  color: variant === 'overview' ? 'mono14' : 'primary1',
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '100%',
});

const ctaArrowStyles = (variant: CurationListProps['variant']): StyleSystemProps => ({
  color: 'white',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: variant === 'overview' ? 'mono14' : 'white',
  borderRadius: '50%',
  size: 6,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  _hover: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
});

export const styles = {
  container: containerStyles,
  itemButton: itemButtonStyles,
  curationsTitle: curationsTitleStyles,
  curationsCta: curationsCtaStyles,
  itemTitle: itemTitleStyles,
  itemDescription: itemDescriptionStyles,
  ctaArrow: ctaArrowStyles,
  photo: photoStyles,
};
